import React, { useMemo, useState, useCallback } from 'react';
import { IGetCustomersShort, EntityStatus } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_CUSTOMERS } from 'appConstants';
import { ISelectOption } from 'components/Select/types';
import FormSelect from 'components/Select/FormSelect';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import { getCustomersShortInfo } from 'backend/customers';
import { TEditDeviceSelectedItem } from 'modules/Devices/EditDevice/editDeviceState/editDeviceContext';

interface ICustomerField {
	setIDCustomer: (value: TEditDeviceSelectedItem) => void;
	IDSelectedCustomer: TEditDeviceSelectedItem;
}

export const CustomerField: React.FC<ICustomerField> = React.memo(({ setIDCustomer, IDSelectedCustomer }) => {
	const { response, loading, refetch, responseError } = useFetchData<IGetCustomersShort, null>(getCustomersShortInfo);
	const [showArchived, setShowArchived] = useState<boolean>(localStorage.getItem(SHOW_ARCHIVED_CUSTOMERS) === 'true');

	const options = useMemo<ISelectOption[]>(() => {
		const items = response?.data.customers || [];
		const visibleItems = showArchived
			? items
			: items.filter((customer) => customer.status === EntityStatus.Active || customer.id === IDSelectedCustomer);

		return visibleItems.map((customer) => ({
			...customer,
			additionalContent: customer.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [response, showArchived, IDSelectedCustomer]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_CUSTOMERS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	return (
		<FormSelect
			testId="CustomerField"
			name="IDSelectedCustomer"
			label="customerLabel"
			options={options}
			fetchError={responseError}
			loading={loading}
			onRefetch={refetch}
			onChange={setIDCustomer}
			keepSearchWithDropdownRender={true}
			dropdownRender={renderSelectMenu}
		/>
	);
});
